import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { Check, Undo } from "@mui/icons-material";
import SelectionActionBar from "ui-components/SelectionActionBar";
import { T } from "utils/backrrTokens";
import moment from "moment";
import { FetchDashboardAnalytics } from "api/dashboardAnalytics";
import { FetchMonetizationAnalytics } from "api/monetizationAnalytics";
import { FetchPlatformAnalytics } from "api/platformAnalytics";
import MonetizationBand from "./MonetizationBand";
import VariantSwitcher from "../variants/VariantSwitcher";
import CustomizeDrawer from "./CustomizeDrawer";
import useDashboardLayout from "../layout/useDashboardLayout";
import VariantMotion from "../variants/shared/motion";
import DashboardA from "../variants/DashboardA";
import DashboardB from "../variants/DashboardB";
import DashboardC from "../variants/DashboardC";
import KpiStrip from "./KpiStrip";
import RangeChips, { rangeForDays } from "./RangeChips";
import DailyActivityChart from "./DailyActivityChart";
import PipelineCard from "./PipelineCard";
import BreakdownPanel from "./BreakdownPanel";
import CampaignTable from "./CampaignTable";
import Disclosure from "./Disclosure";

const DEFAULT_RANGE_DAYS = 1;

// Only the directions that exist render; anything else falls through to the
// shipped dashboard rather than a blank screen.
const VARIANT_VIEWS = {
  a: (props) => <DashboardA {...props} />,
  b: (props) => <DashboardB {...props} />,
  c: (props) => <DashboardC {...props} />,
};

// The page reads top to bottom in order of how often a decision depends on it:
// the four numbers, then where the invitations stand and how the days moved,
// then composition and attribution behind disclosures.
//
// The two tabs this replaced made the campaign table a PEER of the whole
// analytics view, which it never was — it answers "where did signups come
// from", one question among several, and it cost every visitor a tab bar to
// ignore. It is now the last section on the one page.
const DashboardAnalytics = ({ variant = "a", onVariantChange = () => {}, onResetView = () => {} }) => {
  const [range, setRange] = useState(() => rangeForDays(DEFAULT_RANGE_DAYS));
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const layoutApi = useDashboardLayout();
  // The variant choice lives in the page frame (views/dashboard/index.js): it
  // decides whether the breadcrumb header renders, so it has to hold the state.

  // Two services, one range. The marketplace half (startups, investors,
  // pipeline) lives in investor-backend; plans, subscriptions and assessments
  // live in startup-backend. Both bound the day on Asia/Kolkata and both are
  // sent the same from/to, so the halves describe one window — and either can
  // fail without blanking the other.
  const { data, isLoading, isFetching, isError, refetch } = FetchDashboardAnalytics(range);
  const {
    data: monetization,
    isLoading: isLoadingMonetization,
    isError: isMonetizationError,
  } = FetchMonetizationAnalytics(range);
  // Startup platform widgets (profile completion, uploads, sessions, views,
  // rounds, data rooms): a third call to startup-backend, same range. Each of
  // those widgets shows its own loading and error state, so this never holds up
  // or blanks the rest of the page.
  const {
    data: platform,
    isLoading: isLoadingPlatform,
    isFetching: isFetchingPlatform,
    isError: isPlatformError,
  } = FetchPlatformAnalytics(range);

  // keepPreviousData holds isLoading false while a new range loads, so without
  // this the page would show the OLD range's figures as if they were the new
  // one's — silently, for however long ~18 collection operations take.
  const isRefreshing = (isFetching && !isLoading) || (isFetchingPlatform && !isLoadingPlatform);

  const spanDays = data?.range
    ? moment(data.range.to).diff(moment(data.range.from), "days") + 1
    : null;

  // The comparison label belongs here, once — not repeated under all four
  // tiles, where it was four lines of identical text.
  const scope = [
    "Startup profiles with a name",
    spanDays ? `deltas vs previous ${spanDays} ${spanDays === 1 ? "day" : "days"}` : null,
    data?.range
      ? `${moment(data.range.from).format("DD MMM")} – ${moment(data.range.to).format("DD MMM")} ${data.range.timezone}`
      : null,
    data?.generated_at ? `updated ${moment(data.generated_at).fromNow()}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const campaignCount = data?.campaigns?.rows?.length ?? 0;
  const population = data?.by_stage?.total;

  // Both controls send the reader back to the top: see views/dashboard/index.js.
  const handleRangeChange = (next) => {
    setRange(next);
    onResetView();
  };

  const handleVariantChange = (key) => {
    onVariantChange(key);
    onResetView();
  };

  // Remount key for the whole content block.
  //
  // The entrances are CSS animations with `backwards` fill, so they run once
  // per mounted element. Without this, changing the range swapped every figure
  // in place: forty bars jumped to new lengths with no motion at all, and the
  // count-ups — which replay only when their element is new — printed the new
  // numbers outright. Keying the block makes React build fresh nodes, so every
  // bar grows and every figure counts up again.
  //
  // It is keyed on the range the DATA came back with, not the one that was
  // clicked, so the replay happens when the new numbers arrive rather than
  // animating the old ones and then silently swapping them. The variant is in
  // the key too, and that one changes immediately — nothing is being fetched.
  //
  // The header is OUTSIDE this block, so the control that was just used keeps
  // its focus and does not flicker.
  const replayKey = `${variant}:${data?.range?.from ?? ""}:${data?.range?.to ?? ""}`;

  return (
    <Box>
      {VARIANT_VIEWS[variant] && <VariantMotion />}
      {/* Header stays spacious while the data below is dense — a deliberate
          density gradient, so the eye lands on the numbers, not the chrome. */}
      {/* Sticky: the range is the one control that governs every number below
          it, so it has to stay reachable while reading them — otherwise
          changing the window means scrolling back to the top first. It bleeds
          past the content padding (negative margin + matching padding) so the
          background covers edge to edge as rows pass underneath. */}
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1.5}
        sx={{
          // Sticky from tablet up. On a phone the controls wrap to three rows
          // (~200px, a quarter of the screen), so there they scroll away with
          // the page instead of covering every card underneath.
          position: { xs: "static", md: "sticky" },
          top: 0,
          zIndex: 3,
          // Mirrors the page shell's px (views/dashboard/index.js): a fixed
          // -4/4 against a 2 gutter would bleed 16px past the viewport on a
          // phone and bring back the horizontal scrollbar.
          marginX: { xs: -2, md: -4 },
          paddingX: { xs: 2, md: 4 },
          paddingTop: 1,
          paddingBottom: 1.5,
          marginBottom: 1.5,
          backgroundColor: T.pageBg,
          borderBottom: `1px solid transparent`,
        }}
      >
        <Typography sx={{ fontSize: "0.75rem", maxWidth: "46rem" }} color={T.inkSubtle}>
          {scope}
        </Typography>
        {/* The range control stays mounted in every state, including the error
            one — otherwise an admin who picks a bad range is stranded with a
            Retry button that re-sends it forever. */}
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <VariantSwitcher value={variant} onChange={handleVariantChange} />
          <Button size="small" variant="outlined" onClick={() => setCustomizeOpen(true)} aria-haspopup="dialog" aria-expanded={customizeOpen} sx={{ textTransform: "none", borderRadius: "8px", color: T.accent, borderColor: T.border }}>
            {layoutApi.dirty ? "Customize · editing" : layoutApi.hiddenCount ? `Customize · ${layoutApi.hiddenCount} hidden` : "Customize"}
          </Button>
          <RangeChips range={range} onChange={handleRangeChange} />
        </Box>
      </Box>

      <CustomizeDrawer open={customizeOpen} onClose={() => setCustomizeOpen(false)} onReopen={() => setCustomizeOpen(true)} api={layoutApi} />
      {/* A drag on the dashboard edits the draft with the drawer closed; this bar saves or discards it. */}
      <SelectionActionBar
        count={layoutApi.dirty && !customizeOpen ? 1 : 0}
        label={layoutApi.saveFailed ? "Could not save the layout" : "Layout changed"}
        clearLabel="Discard layout changes"
        ariaLabel="Unsaved dashboard layout"
        onClear={layoutApi.discard}
        actions={[
          { key: "discard", label: "Discard", icon: <Undo />, onClick: layoutApi.discard },
          { key: "save", label: "Save layout", icon: <Check />, onClick: layoutApi.save, loading: layoutApi.isSaving },
        ]}
      />

      {isError ? (
        <Box
          sx={{
            border: `1px solid ${T.border}`,
            borderRadius: T.radius,
            padding: 3,
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }} gutterBottom>
            Analytics could not be loaded
          </Typography>
          <Typography sx={{ fontSize: "0.78rem" }} color={T.inkSubtle} gutterBottom>
            The request failed. Try a different range, or retry.
          </Typography>
          <Typography
            component="button"
            onClick={() => refetch()}
            sx={{
              background: "none",
              border: 0,
              padding: "4px 8px",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "0.8rem",
              color: T.accent,
              "&:focus-visible": {
                outline: `2px solid ${T.accent}`,
                outlineOffset: "1px",
              },
            }}
          >
            Retry
          </Typography>
        </Box>
      ) : (
        <Box
          key={replayKey}
          sx={{ opacity: isRefreshing ? 0.55 : 1, transition: "opacity 160ms ease", display: "grid", gap: 2 }}
          aria-busy={isRefreshing}
        >
          {VARIANT_VIEWS[variant] ? (
            VARIANT_VIEWS[variant]({
              data,
              monetization,
              platform,
              platformState: { isLoading: isLoadingPlatform, isError: isPlatformError },
              scope,
              view: layoutApi.view,
              onOrderChange: layoutApi.setOrder,
              onItemOrderChange: layoutApi.setItemOrder,
              isLoading: isLoading || isLoadingMonetization,
            })
          ) : (
            <>
          <KpiStrip
            data={data}
            monetization={monetization}
            isLoading={isLoading || isLoadingMonetization}
          />

          {/* Where the invitations stand, beside how the days moved. The
              pipeline is the narrower question, so it takes the narrower
              column; both stack on anything below a desktop. */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "5fr 7fr" },
              gap: 2,
              // stretch, so the row has no ragged void under the shorter card.
              // The pipeline card absorbs the surplus through its legend; it no
              // longer overflows its grid area, which was the reason this was
              // ever set to start.
              alignItems: "stretch",
            }}
          >
            <PipelineCard
              pipeline={data?.pipeline}
              matchRate={data?.kpis?.match_rate}
              isLoading={isLoading}
            />
            <DailyActivityChart daily={data?.daily} isLoading={isLoading} />
          </Box>

          {/* Revenue is its own topic, so it gets its own band rather than
              being filed under the demographic context below. */}
          <MonetizationBand
            data={monetization}
            isLoading={isLoadingMonetization}
            isError={isMonetizationError}
          />

          <Box sx={{ marginTop: 1 }}>
            <Disclosure
              title="Composition"
              hint="How this range's startups break down by country, stage and category. A startup can hold several stages or sectors, so these bars do not add up to the startup count — which is also why they are bars and not a pie."
              summary={
                population != null
                  ? `${population} startups · a startup may hold more than one`
                  : "country · stage · category"
              }
              defaultOpen
            >
              <BreakdownPanel data={data} isLoading={isLoading} />
            </Disclosure>
          </Box>

          <Box sx={{ borderTop: `1px solid ${T.border}`, paddingTop: 2 }}>
            <Disclosure
              title="Campaign Attribution"
              hint="Signups grouped by the UTM campaign they arrived through. Startups are counted whenever they were created, so that column can exceed the range total above."
              summary={
                isLoading
                  ? "signups by source"
                  : `${campaignCount} ${campaignCount === 1 ? "source" : "sources"} · signups attributed to a campaign`
              }
            >
              <CampaignTable rows={data?.campaigns?.rows} isLoading={isLoading} range={data?.range} />
            </Disclosure>
          </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default DashboardAnalytics;
